import { getWorkspaceTransactions } from '@services/workspace';
import useWorkspace from '@hooks/useWorkspace';
import useWorkspacePermissions from '@hooks/workspacePermissions';
import { useWorkspaceTransactions } from '@hooks/getQueries';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Activity, Bot, FileText, Mic, PieChart } from 'lucide-react';
import { useMemo, useState } from 'react';

const ENTITLEMENT_LABELS = {
  ai_interviews: { label: 'Voice interviews', icon: Mic },
  ai_participant_responses: { label: 'AI participant responses', icon: Bot },
  ai_survey_responses: { label: 'AI survey responses', icon: Bot },
  analysis_units: { label: 'Analysis units', icon: PieChart },
  transcription_uploads: { label: 'Transcription uploads', icon: FileText },
};

const EVENT_LABELS = {
  VOICE_INTERVIEW: 'Voice interview completed',
  AI_PARTICIPANT_INTERVIEW_GENERATION: 'AI participant interview response',
  AI_SURVEY_RESPONSE_GENERATION: 'AI survey response',
  SURVEY_ANALYSIS: 'Survey analysis',
  GROUNDED_EMERGENCE: 'Grounded emergence analysis',
};

const formatDate = (value) => {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown time';
  return date.toLocaleString();
};

const formatAmount = (amount) => {
  if (typeof amount !== 'number') return '';
  const abs = Math.abs(amount);
  return amount < 0 ? `-${abs}` : `+${abs}`;
};

const getTone = (amount) => (amount < 0 ? 'error' : 'success');

const getOperationLabel = (operationType) => {
  if (!operationType) return 'Update';
  const normalized = operationType.toLowerCase();
  if (normalized === 'deduct') return 'Deducted';
  if (normalized === 'add') return 'Added';
  if (normalized === 'refund') return 'Refunded';
  if (normalized === 'set') return 'Set';
  if (normalized === 'expire') return 'Expired';
  return 'Update';
};

const toTitleCase = (value) =>
  value
    .toLowerCase()
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatEventLabel = (eventType) => {
  if (!eventType) return 'Entitlement update';
  return EVENT_LABELS[eventType] || toTitleCase(eventType);
};

function ActivityRow({ tx }) {
  const theme = useTheme();
  const entitlementMeta = ENTITLEMENT_LABELS[tx.entitlement_type] || {};
  const Icon = entitlementMeta.icon || Activity;
  const label = entitlementMeta.label || tx.entitlement_type || 'Entitlement';
  const eventLabel = formatEventLabel(tx.event_type);
  const amountLabel = formatAmount(tx.amount);
  const tone = getTone(tx.amount);

  return (
    <ListItem
      disableGutters
      sx={{
        px: 0,
        py: 1.25,
        alignItems: 'flex-start',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={20} color={theme.palette.primary.main} />
      </Box>
      <Box flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography variant="subtitle2" fontWeight={600}>
            {label}
          </Typography>
          {amountLabel && (
            <Chip
              size="small"
              label={`${getOperationLabel(tx.operation_type)} ${amountLabel}`}
              color={tone}
              variant="outlined"
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {eventLabel}
          {tx.study_id ? ` · Study ${tx.study_id}` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDate(tx.created_at)}
        </Typography>
      </Box>
    </ListItem>
  );
}

function ActivityModal({ open, onClose, workspaceId }) {
  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const limit = 15;

  const loadPage = async (nextOffset = 0) => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      const response = await getWorkspaceTransactions(workspaceId, {
        limit,
        offset: nextOffset,
      });
      const newItems = response?.transactions || [];
      setItems((prev) => (nextOffset === 0 ? newItems : [...prev, ...newItems]));
      setOffset(nextOffset + newItems.length);
      setHasMore(newItems.length === limit);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => {
    loadPage(0);
  };

  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      loadPage(offset);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} onEntering={handleOpen} maxWidth="sm" fullWidth>
      <DialogTitle>Usage Activity</DialogTitle>
      <DialogContent dividers>
        {items.length === 0 && !isLoading ? (
          <Typography variant="body2" color="text.secondary">
            No entitlement activity recorded yet.
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((tx, index) => (
              <Box key={tx.id || `${tx.created_at}-${index}`}>
                <ActivityRow tx={tx} />
                {index < items.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
        {isLoading && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={20} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handleLoadMore} disabled={!hasMore || isLoading}>
          {hasMore ? 'Load more' : 'No more activity'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EntitlementActivityCard() {
  const { activeWorkspaceId } = useWorkspace();
  const { canManageBilling, isLoading: isLoadingPermissions } =
    useWorkspacePermissions(activeWorkspaceId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // Usage/entitlements are admin/owner capability (canManageBilling)
  const canView = canManageBilling;

  const { data, isLoading } = useWorkspaceTransactions(
    activeWorkspaceId,
    { limit: 5, offset: 0 },
    canView,
  );

  const transactions = useMemo(() => data?.transactions || [], [data]);

  if (isLoadingPermissions || !canView) {
    return null;
  }

  return (
    <>
      <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box>
            <Typography variant="h6">Usage</Typography>
            <Typography variant="body2" color="text.secondary">
              Recent entitlements used or added for this workspace
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" size="small" onClick={() => setIsExpanded((prev) => !prev)}>
              {isExpanded ? 'Hide' : 'Show'}
            </Button>
            <Button variant="outlined" size="small" onClick={() => setIsModalOpen(true)}>
              View all
            </Button>
          </Stack>
        </Stack>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          ) : transactions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No entitlement activity recorded yet.
            </Typography>
          ) : (
            <List disablePadding>
              {transactions.map((tx, index) => (
                <Box key={tx.id || `${tx.created_at}-${index}`}>
                  <ActivityRow tx={tx} />
                  {index < transactions.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}
        </Collapse>
      </Card>
      <ActivityModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        workspaceId={activeWorkspaceId}
      />
    </>
  );
}

export default EntitlementActivityCard;
